import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {fetchProjects as fetchProjectsService, createProject as createProjectService, updateProject as updateProjectService} from '@/services/projectService';
import { apiRequest } from '@/lib/queryClient';
import {Button} from '@/components/ui/button';
import {Input} from '@/components/ui/input';
import {Select, SelectContent, SelectItem, SelectTrigger, SelectValue} from '@/components/ui/select';
import {Badge} from '@/components/ui/badge';
import {Dialog, DialogTrigger} from '@/components/ui/dialog';
import {Avatar, AvatarFallback} from '@/components/ui/avatar';
import {Tooltip, TooltipContent, TooltipProvider, TooltipTrigger} from '@/components/ui/tooltip';
import {
  Activity,
  AlertCircle,
  ArrowRight,
  ArrowUpDown,
  Building2,
  Check,
  ChevronLeft,
  ChevronRight,
  Clock,
  Eye,
  Grid2X2,
  List,
  LogOut,
  Plus,
  PlusCircle,
  Search,
  Settings,
  Share2,
  TrendingUp,
  User,
  X
} from 'lucide-react';
import {Link as WouterLink, useLocation} from 'wouter';
import {useAuth} from '@/contexts/AuthContext';
import {useToast} from '@/hooks/use-toast';
import ProjectCard from '@/components/projects/ProjectCard.tsx';
import redwoodMark from "@/assets/Redwood_LogoMark_White.png";
import redwoodWordmark from "@/assets/Redwood_Logo_WhiteOnly.png";
import {Project} from "@shared/interfaces/project.interface.ts";
import EditProjectModal from "@/components/projects/EditProjectModal.tsx";
import DeleteProjectModal from '@/components/projects/DeleteProjectModal';
import {ProjectPayload} from "@shared/interfaces/projectPayload.interface.ts";
import NewProjectModal from '@/components/projects/NewProjectModal';
import {MetricCard} from "@/components/ui/MetricCard.tsx";
import { calculateProjectProgress, getProjectStatus } from '@/utils/projectProgress.ts';
import { useQueryClient } from '@tanstack/react-query';
import { getProjectLoadingState } from './ProjectSelectionScreen.utils';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { normalizeYearlyCosts, ZERO_PROJECT_YEARLY_COSTS } from '@shared/constants/projectYearlyCosts.ts';

const createDefaultYearlyCosts = () => normalizeYearlyCosts();
const createZeroYearlyCosts = () => normalizeYearlyCosts(ZERO_PROJECT_YEARLY_COSTS, ZERO_PROJECT_YEARLY_COSTS);


// Project List Table Component
function ProjectListTable({ projects }: { projects: Project[] }) {
  const [sortKey, setSortKey] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const sortedProjects = useMemo(() => {
    return [...projects].sort((a, b) => {
      let aValue: any, bValue: any;

      switch (sortKey) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'client_org':
          aValue = a.client_org?.toLowerCase() || '';
          bValue = b.client_org?.toLowerCase() || '';
          break;
          // case 'owner_name':
          //   aValue = a.owner_name?.toLowerCase() || '';
          //   bValue = b.owner_name?.toLowerCase() || '';
          //   break;
        case 'created_at':
          aValue = new Date(a.created_at).getTime();
          bValue = new Date(b.created_at).getTime();
          break;
          case 'completion':
            // Use shared utility function for consistent progress calculation
            aValue = calculateProjectProgress(a);
            bValue = calculateProjectProgress(b);
            break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [projects, sortKey, sortDirection]);

  const handleSort = (key: string) => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('asc');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  // Use shared utility function for consistent status calculation
  const getModuleStatus = getProjectStatus;

  const renderModuleActions = (project: Project, module: 'value_assessment') => {
    const completion = project.module_completion?.[module] || 0;
    const status = getModuleStatus(completion);
    const moduleLabel = 'Value Assessment';

    return (
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Badge
                variant="outline"
                className={`text-xs ${
                    status === 'complete' ? 'bg-done text-white ' :
                        status === 'in-progress' ? 'bg-orange text-forestBlack' :
                            'bg-transparent text-gray-300 border-gray-500'
                }`}
            >
              {moduleLabel}
            </Badge>
            {status === 'complete' && <Check className="w-4 h-4 text-done" size={16} aria-label="Complete" />}
            {status === 'in-progress' && <Clock className="w-4 h-4 text-orange" size={16} aria-label="In Progress" />}
            {status === 'not-started' && <X className="w-4 h-4 text-gray-400" size={16} aria-label="Not Started" />}
          </div>

          <div className="flex items-center gap-1">
            {status === 'complete' ? (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <WouterLink to={`/value-assessment/${project.id}/value-assessment-summary`}>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-6 w-6 text-gray-400 hover:text-white"
                            tabIndex={0}
                        >
                          <Eye className="w-4 h-4" size={16} />
                        </Button>
                      </WouterLink>
                    </TooltipTrigger>
                    <TooltipContent>View results</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
            ) : (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <WouterLink to={`/value-assessment/${project.id}/intro`}>
                        <Button
                            variant="ghost"
                            size="sm"
                            className="p-1 h-6 w-6 text-gray-400 hover:text-white"
                            tabIndex={0}
                        >
                          <ArrowRight className="w-4 h-4" size={16} />
                        </Button>
                      </WouterLink>
                    </TooltipTrigger>
                    <TooltipContent>Start module</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
            )}

            {/* Remove maturity sharing - VALUE ASSESSMENT ONLY */}
            {false && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                          variant="ghost"
                          size="sm"
                          className="p-1 h-6 w-6 text-gray-400 hover:text-white"
                          onClick={() => {
                            // VALUE ASSESSMENT ONLY - no sharing
                          }}
                          tabIndex={0}
                      >
                        <Share2 className="w-4 h-4" size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Share link</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
            )}
          </div>
        </div>
    );
  };

  const SortableHeader = ({ label, sortKey: key }: { label: string; sortKey: string }) => (
      <th
          className="px-3 py-3 text-left text-foreground font-medium cursor-pointer hover:bg-muted/10 transition-colors"
          onClick={() => handleSort(key)}
      >
        <div className="flex items-center gap-1">
          {label}
          <ArrowUpDown className="w-3 h-3" />
        </div>
      </th>
  );

  return (
      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg overflow-hidden">
        <table className="min-w-full table-auto text-sm">
          <thead className="bg-muted/20">
          <tr>
            <SortableHeader label="Project Title" sortKey="name" />
            <SortableHeader label="Client" sortKey="client_org" />
            <SortableHeader label="Owner" sortKey="owner_name" />
            <SortableHeader label="Created" sortKey="created_at" />
            <SortableHeader label="Completion %" sortKey="completion" />
            <th className="px-3 py-3 text-left text-white font-medium">Status</th>
            <th className="px-3 py-3 text-left text-white font-medium">Modules</th>
          </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
          {sortedProjects.map((project) => {
            // Use shared utility function for consistent progress calculation
            const totalCompletion = calculateProjectProgress(project);

            return (
                <tr key={project.id} className="hover:bg-muted/10 transition-colors">
                  <td className="px-3 py-3 max-w-lg">
                    <div>
                      <div className="text-white font-medium">{project.name}</div>
                      <div className="text-gray-400 text-xs">{project.description}</div>
                    </div>
                  </td>
                  <td className="px-3 py-3 text-white">{project.client_org || 'N/A'}</td>
                  <td className="px-3 py-3 text-white">{project.owner_email || 'Admin User'}</td>
                  <td className="px-3 py-3 text-gray-300">{formatDate(project.created_at)}</td>
                  <td className="px-3 py-3">
                    <div className="flex items-center gap-2">
                      <div className="text-white font-medium">{totalCompletion}%</div>
                      <div className="w-16 bg-gray-700 rounded-full h-2">
                        <div
                            className="bg-gradient-to-r from-primary to-primary/80 h-2 rounded-full transition-all duration-300"
                            style={{ width: `${totalCompletion}%` }}
                        />
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-3">
                    {project.summary_data?.valueSummary?.meta?.externalCompleted ? (
                      <span className="text-green-500 font-semibold">✓ Submitted</span>
                    ) : (
                      <span className="text-gray-400 italic">Pending</span>
                    )}
                  </td>
                  <td className="px-3 py-3">
                    <div className="space-y-2">
                      {renderModuleActions(project, 'value_assessment')}
                    </div>
                  </td>
                </tr>
            );
          })}
          </tbody>
        </table>
      </div>
  );
}

export default function ProjectSelectionScreen() {
  const [location, setLocation] = useLocation();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(6);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProjectCount, setTotalProjectCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    completed: 0,
    inProgress: 0,
    notStarted: 0,
    submitted: 0,
    avgCompletion: 0,
  });
  const [availableClients, setAvailableClients] = useState<string[]>([]);
  const [availableOwners, setAvailableOwners] = useState<string[]>([]);
  const [showNewProjectModal, setShowNewProjectModal] = useState(false);

  // Control bar state
  const [selectedClients, setSelectedClients] = useState<string[]>([]);
  const [selectedOwner, setSelectedOwner] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'recent' | 'alphabetical' | 'completion'>('recent');
  const [viewMode, setViewMode] = useState<'tile' | 'list'>('tile');
  const [showEditProjectModal, setShowEditProjectModal] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [newProject, setNewProject] = useState<ProjectPayload>({
    name: '',
    description: '',
    client_org: '',
    owner_email: '',
    collaborators: [],
    includePricingInValueAssessment: false,
    assessment_contact_email: '',
    salesforce_opportunity_id: '',
    // NEW FIELDS
    primary_contact_name: '',
    primary_contact_email: '',
    client_collaborators: [],
    nda_status: 'needs_nda',
    export_options_enabled: true,
    show_financial_metrics: false,
    yearly_costs: createDefaultYearlyCosts(),
  });
  const [editProject, setEditProject] = useState<ProjectPayload>({
    name: '',
    description: '',
    client_org: '',
    collaborators: [],
    includePricingInValueAssessment: false,
    assessment_contact_email: '',
    salesforce_opportunity_id: '',
    // NEW FIELDS
    primary_contact_name: '',
    primary_contact_email: '',
    client_collaborators: [],
    nda_status: 'needs_nda',
    export_options_enabled: true,
    show_financial_metrics: false,
    yearly_costs: createZeroYearlyCosts(),
  });
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState<Project | null>(null);
  const { user, logout, isSuperAdmin } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const fetchProjects = useCallback(async (pageToFetch: number = 1) => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await fetchProjectsService({
        page: pageToFetch,
        pageSize: itemsPerPage,
        search: debouncedSearchTerm,
        clients: selectedClients,
        owner: selectedOwner !== 'all' ? selectedOwner : undefined,
        status: selectedStatus !== 'all' ? selectedStatus : undefined,
        sort: sortBy,
        includeFilters: true,
      });

      if (Array.isArray(response)) {
        const projectsWithStatus = response.map((project: any) => {
          const mc = project.module_completion ?? {};

          const valueAssessmentCompletion = typeof mc.value_assessment === 'number' ? mc.value_assessment : 0;
          const maturityCompletion = typeof mc.maturity === 'number' ? mc.maturity : 0;

          const completions: number[] = [];
          if (typeof valueAssessmentCompletion === 'number') completions.push(valueAssessmentCompletion);
          if (typeof maturityCompletion === 'number') completions.push(maturityCompletion);

          const totalModules = completions.length || 1;
          const avgCompletion = Math.round(
            completions.reduce((sum, v) => sum + v, 0) / totalModules
          );
          const completedModules = completions.filter(v => v >= 100).length;

          let status: 'Completed' | 'In Progress' | 'Not Started';
          if (avgCompletion === 100 || completedModules === totalModules) {
            status = 'Completed';
          } else if (avgCompletion > 0) {
            status = 'In Progress';
          } else {
            status = 'Not Started';
          }

          return {
            ...project,
            status,
            overallProgress: avgCompletion,
            module_completion: { ...mc },
          };
        });

        setProjects(projectsWithStatus);
        setTotalProjectCount(projectsWithStatus.length);
        const computedTotalPages = Math.max(Math.ceil(projectsWithStatus.length / itemsPerPage), 1);
        setTotalPages(computedTotalPages);
        setStats({
          total: projectsWithStatus.length,
          completed: projectsWithStatus.filter(project => project.status === 'Completed').length,
          inProgress: projectsWithStatus.filter(project => project.status === 'In Progress').length,
          notStarted: projectsWithStatus.filter(project => project.status === 'Not Started').length,
          submitted: projectsWithStatus.filter(project => Boolean(project.summary_data?.valueSummary?.meta?.externalCompleted)).length,
          avgCompletion: projectsWithStatus.length > 0
            ? Math.round(projectsWithStatus.reduce((sum, project) => sum + (project.overallProgress ?? 0), 0) / projectsWithStatus.length)
            : 0,
        });
        setAvailableClients(Array.from(new Set(projectsWithStatus.map(project => project.client_org).filter((client): client is string => Boolean(client)))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
        setAvailableOwners(Array.from(new Set(projectsWithStatus.map(project => project.owner_email).filter((owner): owner is string => Boolean(owner)))).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' })));
        setCurrentPage(prev => {
          const targetPage = Number.isFinite(pageToFetch) ? pageToFetch : 1;
          if (targetPage < 1) return 1;
          if (targetPage > computedTotalPages) return computedTotalPages;
          return targetPage;
        });
        setHasLoadedInitialData(true);
      } else {
        const { data = [], pagination, stats: serverStats, filterOptions } = response ?? {};
        setProjects(data as Project[]);
        setTotalProjectCount(pagination?.totalProjects ?? (Array.isArray(data) ? data.length : 0));
        setTotalPages(pagination?.totalPages ?? 1);
        setCurrentPage(pagination?.page ?? pageToFetch);
        setStats({
          total: serverStats?.total ?? (Array.isArray(data) ? data.length : 0),
          completed: serverStats?.completed ?? (Array.isArray(data) ? data.filter((project: any) => project.status === 'Completed').length : 0),
          inProgress: serverStats?.inProgress ?? (Array.isArray(data) ? data.filter((project: any) => project.status === 'In Progress').length : 0),
          notStarted: serverStats?.notStarted ?? (Array.isArray(data) ? data.filter((project: any) => project.status === 'Not Started').length : 0),
          submitted: serverStats?.submitted ?? (Array.isArray(data) ? data.filter((project: any) => Boolean(project.summary_data?.valueSummary?.meta?.externalCompleted)).length : 0),
          avgCompletion: serverStats?.avgCompletion ?? (Array.isArray(data) && data.length > 0
            ? Math.round((data as any[]).reduce((sum, project) => sum + (project.overallProgress ?? 0), 0) / data.length)
            : 0),
        });
        setAvailableClients(filterOptions?.clients ?? []);
        setAvailableOwners(filterOptions?.owners ?? []);
        setHasLoadedInitialData(true);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
      const msg = (error instanceof Error && error.message) ? error.message : String(error);
      if (msg.startsWith('401')) {
        try { setLocation('/login'); } catch {}
        return;
      }
      setError('Database connection issue. Please refresh the page.');
    } finally {
      setIsLoading(false);
    }
  }, [itemsPerPage, debouncedSearchTerm, selectedClients, selectedOwner, selectedStatus, sortBy, setLocation]);

  useEffect(() => {
    if (!user) {
      return;
    }
    fetchProjects(currentPage).catch(console.error);
  }, [user, currentPage, fetchProjects]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedClients, selectedOwner, selectedStatus, itemsPerPage]);

  const uniqueClients = availableClients;
  const uniqueOwners = availableOwners;

  const currentProjects = projects;

  const handleCreateProject = async () => {
    try {
      const yearlyCosts = normalizeYearlyCosts(newProject.yearly_costs);
      // Ensure all required fields are properly formatted and optional fields have fallbacks
      const payload = {
        name: newProject.name.trim(),
        description: newProject.description?.trim() || "",
        client_org: newProject.client_org?.trim() || "",
        // Ensure backend receives the owner email tied to the current user
        owner_email: user?.email || newProject.owner_email || undefined,
        salesforce_opportunity_id: newProject.salesforce_opportunity_id?.trim() || "",
        assessment_contact_email: newProject.assessment_contact_email?.trim() || "",
        includePricingInValueAssessment: Boolean(newProject.includePricingInValueAssessment || newProject.show_financial_metrics),
        // New fields persisted on create so Edit modal reflects entered data
        primary_contact_name: newProject.primary_contact_name?.trim() || '',
        primary_contact_email: (newProject.primary_contact_email || newProject.assessment_contact_email || '').trim(),
        client_collaborators: (newProject.client_collaborators || []).map(c => ({ name: c.name?.trim() || '', email: c.email?.trim() || '' })),
        nda_status: newProject.nda_status || 'needs_nda',
        nda_reference: newProject.nda_reference?.trim() || undefined,
        nda_signed_date: newProject.nda_signed_date || undefined,
        export_options_enabled: newProject.export_options_enabled ?? true,
        show_financial_metrics: !!newProject.show_financial_metrics,
        yearly_costs: yearlyCosts,
        // Key dates
        assessment_scheduled_at: newProject.assessment_scheduled_at || undefined,
        presentation_scheduled_at: newProject.presentation_scheduled_at || undefined,
      } as ProjectPayload;

      const project = await createProjectService(payload);

      toast({
        title: "Success",
        description: "Project created successfully",
      });

      setShowNewProjectModal(false);
      setNewProject({
        name: '',
        description: '',
        client_org: '',
        owner_email: '',
        collaborators: [],
        includePricingInValueAssessment: false,
        assessment_contact_email: '',
        salesforce_opportunity_id: '',
        // NEW FIELDS
        primary_contact_name: '',
        primary_contact_email: '',
        client_collaborators: [],
        nda_status: 'needs_nda',
        export_options_enabled: true,
        show_financial_metrics: false,
        yearly_costs: createDefaultYearlyCosts(),
      });

      await fetchProjects();
    } catch (error) {
      console.error("Project creation error:", error instanceof Error ? error.message : error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "An error occurred while creating the project",
        variant: "destructive",
      });
    }
  };

  const handleEditProject = (project: Project) => {
    setEditingProject(project);
    setEditProject({
      name: project.name,
      description: project.description || '',
      client_org: project.client_org || '',
      // DO NOT include owner_email - it gets transformed by GET endpoint and shouldn't be editable
      collaborators: project.collaborators || [],
      includePricingInValueAssessment: (project as any).value_assessment_pricing_enabled || false,
      assessment_contact_email: (project as any).assessment_contact_email || '',
      salesforce_opportunity_id: project.salesforce_opportunity_id || '',

      // NEW FIELDS
      primary_contact_name: (project as any).primary_contact_name || '',
      primary_contact_email: (project as any).primary_contact_email || '',
      client_collaborators: (project as any).client_collaborators || [],
      nda_status: (project as any).nda_status === 'signed' ? 'already_signed' : 'needs_nda',
      nda_reference: (project as any).nda_agreement_id || '',
      nda_signed_date: (project as any).nda_signed_at ? new Date((project as any).nda_signed_at).toISOString().split('T')[0] : '',
      export_options_enabled: (project as any).export_options_enabled ?? true,
      show_financial_metrics: (project as any).show_financial_metrics ?? false,
      // Seed Key Dates so the modal shows them immediately
      assessment_scheduled_at: (project as any).assessment_scheduled_at || '',
      assessment_completed_at: (project as any).assessment_completed_at || '',
      presentation_scheduled_at: (project as any).presentation_scheduled_at || '',
      yearly_costs: (project as any).yearly_costs ? normalizeYearlyCosts((project as any).yearly_costs, ZERO_PROJECT_YEARLY_COSTS) : createZeroYearlyCosts(),
    });
    setShowEditProjectModal(true);
  };



  const handleUpdateProject = async () => {
    if (!editingProject) return;

    try {
      const yearlyCosts = normalizeYearlyCosts(editProject.yearly_costs);
      const response = await updateProjectService(editingProject.id, {
        name: editProject.name,
        description: editProject.description,
        client_org: editProject.client_org,
        owner_email: editProject.owner_email,
        collaborators: (editProject.collaborators || [])
            .map((email: string) => email.trim())
            .filter((email: string) => email.length > 0),
        // Align with EditProjectModal toggle; keep server flag in sync
        value_assessment_pricing_enabled: !!editProject.show_financial_metrics,
        assessment_contact_email: editProject.assessment_contact_email,
        salesforce_opportunity_id: editProject.salesforce_opportunity_id,
        
        // NEW FIELDS
        primary_contact_name: editProject.primary_contact_name,
        primary_contact_email: editProject.primary_contact_email,
        client_collaborators: editProject.client_collaborators,
        nda_status: editProject.nda_status,
        nda_reference: editProject.nda_reference,
        nda_signed_date: editProject.nda_signed_date,
        export_options_enabled: editProject.export_options_enabled,
        show_financial_metrics: !!editProject.show_financial_metrics,
        includePricingInValueAssessment: !!editProject.show_financial_metrics,
        yearly_costs: yearlyCosts,
        // Key Dates
        assessment_scheduled_at: editProject.assessment_scheduled_at || undefined,
        presentation_scheduled_at: editProject.presentation_scheduled_at || undefined
      } as ProjectPayload);

      if (response) {
        toast({
          title: "Success",
          description: "Project updated successfully",
        });
        // Ensure dependent pages see the latest project details immediately
        try {
          // Update the specific project details cache used by useProjectDetails(projectId)
          queryClient.setQueryData(['/api/projects', editingProject.id], (old: any) => ({
            ...(old || {}),
            ...(response || {}),
            // Ensure export_options_enabled reflects the latest toggle
            export_options_enabled: editProject.export_options_enabled,
          }));
          // Force a fresh fetch next time the details page mounts (refetchOnMount is disabled globally)
          queryClient.removeQueries({ queryKey: ['/api/projects', editingProject.id] });
          queryClient.invalidateQueries({ queryKey: ['/api/projects', editingProject.id] });
          // Also refresh the projects list grid
          queryClient.invalidateQueries({ queryKey: ['/api/projects'] });
        } catch (cacheErr) {
          console.warn('Project cache update failed:', cacheErr);
        }
        setShowEditProjectModal(false);
        setEditingProject(null);
        setEditProject({
          name: '',
          description: '',
          client_org: '',
          collaborators: [],
          includePricingInValueAssessment: false,
          assessment_contact_email: '',
          salesforce_opportunity_id: '',
          // NEW FIELDS
          primary_contact_name: '',
          primary_contact_email: '',
          client_collaborators: [],
          nda_status: 'needs_nda',
          export_options_enabled: true,
          show_financial_metrics: false,
          yearly_costs: createZeroYearlyCosts(),
        });
        fetchProjects();
      } else {
        toast({
          title: "Error",
          description: "Failed to update project",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An error occurred while updating the project",
        variant: "destructive",
      });
    }
  };

  const handleViewProject = (projectId: string) => {
    setLocation(`/project/${projectId}`);
  };

  const requestDeleteProject = (project: Project) => {
    if (!isSuperAdmin) return;
    setProjectToDelete(project);
    setShowDeleteConfirmModal(true);
  };

  const handleDeleteProject = async (project: Project) => {
    if (!isSuperAdmin) return;
    try {
      await apiRequest(`/api/projects/${project.id}`, { method: 'DELETE' });
      toast({ title: "Success", description: "Project deleted successfully" });
      fetchProjects();
    } catch (error) {
      console.error("Project deletion error:", error instanceof Error ? error.message : error);
      toast({ title: "Error", description: error instanceof Error ? error.message : "An error occurred while deleting the project", variant: "destructive" });
    }
  };

  // Calculate metrics for dashboard cards
  const totalProjects = totalProjectCount;
  const completedProjects = stats.completed;
  const inProgressProjects = stats.inProgress;
  const avgCompletion = stats.avgCompletion;

  const loadingState = getProjectLoadingState({ isLoading, hasLoadedInitialData });
  const isGridLoading = loadingState === 'grid';

  if (loadingState === 'initial') {
    return (
        <div className="min-h-screen bg-forestBlack flex items-center justify-center">
          <div className="text-center">
            <div className="text-white text-xl font-light font-['Archivo'] mb-4">Loading projects...</div>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4AE2C9] mx-auto"></div>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="min-h-screen bg-forestBlack flex items-center justify-center">
          <div className="text-center max-w-md mx-auto p-6">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-white mb-4">Connection Issue</h2>
            <p className="text-gray-300 mb-6">{error}</p>
            <div className="space-y-3">
              <Button
                  onClick={fetchProjects}
                  className="bg-primary text-forestBlack hover:bg-primaryHover w-full"
              >
                Try Again
              </Button>
              <Button
                  variant="outline"
                  onClick={() => window.location.reload()}
                  className="border-white/20 text-white hover:bg-white/10 w-full"
              >
                Refresh Page
              </Button>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="min-h-screen vm-bg-main vm-text-primary">
        {/* Header */}
        <div className="border-b border-[#1F3B46] bg-forestBlack">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <img src={redwoodMark} alt="Redwood" className="h-8 w-8" />
                <img src={redwoodWordmark} alt="Redwood" className="h-6" />
              </div>
              <div className="flex items-center space-x-4">


                {user && (
                    <div className="flex items-center space-x-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-[#4AE2C9] text-[#0D1224] text-sm font-semibold">
                          {user.username?.substring(0, 2).toUpperCase() || 'AD'}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-white text-sm font-medium">{user.username}</span>
                      {isSuperAdmin && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setLocation('/admin')}
                            className="border-white/20 text-white hover:bg-white/10"
                        >
                          <Settings className="w-4 h-4 mr-2" />
                          Admin Dashboard
                        </Button>
                      )}
                      <Button
                          variant="outline"
                          size="sm"
                          onClick={logout}
                          className="border-white/20 text-white hover:bg-white/10"
                      >
                        <LogOut className="w-4 h-4 mr-2" />
                        Logout
                      </Button>
                    </div>
                )}
                {!user && (
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setLocation('/login')}
                        className="border-white/20 text-white hover:bg-white/10"
                    >
                      <User className="w-4 h-4 mr-2" />
                      Admin Login
                    </Button>
                )}
              </div>

              <div className="flex items-center space-x-4">
              <Dialog open={showNewProjectModal} onOpenChange={setShowNewProjectModal}>
                <DialogTrigger asChild>
                  <Button className="bg-primary text-forestBlack px-4 py-2 rounded-md flex items-center gap-2 hover:bg-[#27C4C5] ">
                    <PlusCircle className="w-4 h-4" />
                    New Project
                  </Button>
                </DialogTrigger>
              </Dialog>
              </div>
            </div>
          </div>
        </div>

        <div className="container mx-auto px-6 py-8">
          {/* Title Section */}
          <div className="text-center mb-8">
            <h1 className="text-2xl font-semibold text-slate-800 dark:text-white mb-4 font-['Archivo']">
              Start your R2R Value Assessment Journey
            </h1>
            <p className="text-lg text-[#A0B0C0] max-w-3xl mx-auto font-light font-['Archivo']">
              Create a new project or select an existing one to continue your assessment
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <MetricCard
                icon={Building2}
                value={totalProjects}
                label="Total Projects"
            />

            <MetricCard
                icon={Check}
                value={completedProjects}
                label="Completed"
            />

            <MetricCard
                icon={Activity}
                value={inProgressProjects}
                label="In Progress"
            />

            <MetricCard
                icon={TrendingUp}
                value={`${avgCompletion}%`}
                label="Avg Completion"
            />

          </div>

          {/* Enhanced Control Bar */}
          <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-2 border-teal-500/30 shadow-xl shadow-teal-900/30 backdrop-blur-sm rounded-lg mb-6">
            {/* Search and Filter Bar */}
            <div className="flex flex-wrap items-center gap-4 px-4 py-3 border-b border-teal-500/20">
              {/* Search Input */}
              <div className="flex-1 min-w-[250px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 vm-text-secondary w-4 h-4" />
                  <Input
                      placeholder="Search projects or clients"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 vm-bg-main vm-border vm-text-primary placeholder:vm-text-secondary"
                  />
                </div>
              </div>

              {/* Client Multi-Select */}
              <div className="min-w-[180px]">
                <Select
                    value={selectedClients.length > 0 ? selectedClients[0] : "none"}
                    onValueChange={(value) => {
                      if (value && value !== "none" && !selectedClients.includes(value)) {
                        setSelectedClients([...selectedClients, value]);
                      }
                    }}
                >
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Client Filter" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select Company</SelectItem>
                    {uniqueClients.map(client => (
                        <SelectItem key={client} value={client}>{client}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Owner Filter */}
              <div className="min-w-[150px]">
                <Select value={selectedOwner} onValueChange={setSelectedOwner}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Owner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Owners</SelectItem>
                    {uniqueOwners.map(owner => (
                        <SelectItem key={owner} value={owner}>{owner}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Module filter removed - all projects are VALUE ASSESSMENT ONLY */}

              {/* Status Filter */}
              <div className="min-w-[140px]">
                <Select value={selectedStatus} onValueChange={setSelectedStatus}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    <SelectItem value="Not Started">Not Started</SelectItem>
                    <SelectItem value="In Progress">In Progress</SelectItem>
                    <SelectItem value="Completed">Complete</SelectItem>
                    <SelectItem value="Submitted">Submitted</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Sort Dropdown */}
              <div className="min-w-[140px]">
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="bg-white/10 border-white/20 text-white">
                    <ArrowUpDown className="w-4 h-4 mr-2" />
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="alphabetical">Alphabetical</SelectItem>
                    <SelectItem value="completion">Completion %</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex items-center gap-2 bg-white/10 rounded-md p-1">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode('tile')}
                    className={`p-2 ${viewMode === 'tile' ? 'bg-primary text-black' : 'text-white hover:bg-white/20'}`}
                    aria-label="Tile view"
                >
                  <Grid2X2 className="w-4 h-4" />
                </Button>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-primary text-black' : 'text-white hover:bg-white/20'}`}
                    aria-label="List view"
                >
                  <List className="w-4 h-4" />
                </Button>
              </div>
            </div>

            {/* Selected Filters Display */}
            {(selectedClients.length > 0 || (selectedOwner && selectedOwner !== 'all') || (selectedStatus && selectedStatus !== 'all')) && (
                <div className="px-4 py-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-400">Active filters:</span>
                  {selectedClients.map(client => (
                      <Badge key={client} variant="secondary" className="bg-primary text-black">
                        {client}
                        <button
                            onClick={() => setSelectedClients(selectedClients.filter(c => c !== client))}
                            className="ml-1 hover:bg-black/20 rounded-full p-0.5"
                        >
                          ×
                        </button>
                      </Badge>
                  ))}
                  {selectedOwner && selectedOwner !== 'all' && (
                      <Badge variant="secondary" className="bg-primary text-black">
                        Owner: {selectedOwner}
                        <button onClick={() => setSelectedOwner('all')} className="ml-1">×</button>
                      </Badge>
                  )}
                  {/* Module filters removed - VALUE ASSESSMENT ONLY */}
                  {selectedStatus && selectedStatus !== 'all' && (
                      <Badge variant="secondary" className="bg-primary text-black">
                        Status: {selectedStatus}
                        <button onClick={() => setSelectedStatus('all')} className="ml-1">×</button>
                      </Badge>
                  )}
                  <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedClients([]);
                        setSelectedOwner('all');
                        setSelectedStatus('all');
                        setSearchTerm('');
                      }}
                      className="text-gray-400 hover:text-white text-sm"
                  >
                    Clear all
                  </Button>
                </div>
            )}
          </div>



          {/* Action Bar */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
            <div className="flex items-center gap-4">
              <h2 className="text-xl font-semibold text-white">
                Projects ({totalProjects})
              </h2>
            </div>

            <div className="flex items-center gap-3">
              <Select value={itemsPerPage.toString()} onValueChange={(value) => setItemsPerPage(Number(value))}>
                <SelectTrigger className="w-32 bg-white/10 border-white/20 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 per page</SelectItem>
                  <SelectItem value="12">12 per page</SelectItem>
                  <SelectItem value="18">18 per page</SelectItem>
                  <SelectItem value="24">24 per page</SelectItem>
                </SelectContent>
              </Select>

              <NewProjectModal
                  isOpen={showNewProjectModal}
                  onClose={() => setShowNewProjectModal(false)}
                  newProject={newProject}
                  setNewProject={setNewProject}
                  onSubmit={handleCreateProject}
              />

              {/* Edit Project Modal */}
              <EditProjectModal
                  isOpen={showEditProjectModal}
                  onClose={() => setShowEditProjectModal(false)}
                  onSubmit={handleUpdateProject}
                  editProject={editProject}
                  setEditProject={setEditProject}
                  isSubmitting={false}
                  projectData={editingProject}
              />
            </div>
          </div>

          {/* Projects Grid */}
          {currentProjects.length === 0 ? (
              <div className="relative">
                {isGridLoading && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-forestBlack/80 backdrop-blur-sm">
                      <div className="text-white text-sm font-medium">Updating projects…</div>
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4AE2C9]"></div>
                    </div>
                )}
                <div className={`text-center py-12 ${isGridLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                  <Building2 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-white mb-2">No projects found</h3>
                  <p className="text-gray-300 mb-6">
                    {totalProjects > 0
                        ? "Try adjusting your search or filters"
                        : "Create your first R2R assessment project to get started"
                    }
                  </p>
                  <Button
                      onClick={() => setShowNewProjectModal(true)}
                      className="bg-[#4AE2C9] text-[#0D1224] hover:bg-[#4AE2C9]/90"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Project
                  </Button>
                </div>
              </div>
          ) : (
              <>
                <div className="relative">
                  {isGridLoading && (
                      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-forestBlack/80 backdrop-blur-sm">
                        <div className="text-white text-sm font-medium">Updating projects…</div>
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#4AE2C9]"></div>
                      </div>
                  )}
                  {viewMode === 'tile' ? (
                      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8 ${isGridLoading ? 'opacity-50 pointer-events-none' : ''}`}>
                        {currentProjects.map((project) => (
                            <div key={project.id} data-cy="project-card" data-testid="project-card">

                              <ProjectCard
                                  key={project.id}
                                  project={project}
                                  onEdit={handleEditProject}
                                  onViewProject={handleViewProject}
                                  onDelete={requestDeleteProject}
                                  isSuperAdmin={isSuperAdmin}
                              />
                            </div>
                        ))}
                      </div>
                  ) : (
                      <div className={isGridLoading ? 'opacity-50 pointer-events-none' : ''}>
                        <ProjectListTable projects={currentProjects} />
                      </div>
                  )}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="bg-gradient-to-br from-slate-800/60 to-slate-900/80 border-2 border-teal-500/30 shadow-xl rounded-lg p-4 mt-8">
                      <div className="flex items-center justify-center space-x-6">
                        <Button
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                            disabled={currentPage === 1}
                            className="border-white/20 text-white hover:bg-white/10"
                        >
                          <ChevronLeft className="w-4 h-4 mr-2" />
                          Previous
                        </Button>

                        <div className="flex items-center space-x-2">
                          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                              <Button
                                  key={page}
                                  variant={currentPage === page ? "default" : "outline"}
                                  onClick={() => setCurrentPage(page)}
                                  className={
                                    currentPage === page
                                        ? "bg-primary dark:text-forestBlack  hover:bg-[#27C4C5]"
                                        : "border-white/20 dark:text-white text-red-300 hover:bg-white/10"
                                  }
                              >
                                {page}
                              </Button>
                          ))}
                        </div>

                        <Button
                            variant="outline"
                            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                            disabled={currentPage === totalPages}
                            className="border-white/20 text-white hover:bg-primaryHover"
                        >
                          Next
                          <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                )}
              </>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <DeleteProjectModal
            isOpen={showDeleteConfirmModal}
            onClose={() => {
              setShowDeleteConfirmModal(false);
              setProjectToDelete(null);
            }}
            projectToDelete={projectToDelete}
            onConfirm={handleDeleteProject}
        />
      </div>
  );
}
